import React, { useState, useEffect, useRef } from "react";
import { VariableSizeList as List } from "react-window";
import isEqual from "lodash/isEqual";
import moment from "moment";
import DayPicker, { DateUtils } from "react-day-picker";
import classNames from "classnames";

import { useOutsideClickHook } from "utils/outsideClick";
import { getRangeForFilters, getTimeInSeconds } from "utils/payment/helpers";
import { createKeyboardShortcut } from "utils/keyboardShortcuts";
import { onKSelect } from "./withSelect";

import InfoTooltip from "components/InfoTooltip";
import DropdownSearch from "components/Dropdown/DropdownSearch";
import LoadingDots from "components/LoadingDots";
import WarningTooltip from "components/WarningTooltip";
import NonH2kParamTooltip from "components/NonH2kParamTooltip";
import Compass from "components/Compass";
import Tooltip from "components/Tooltip";
import Button from "components/Button";

import classes from "./style.module.scss";
import sharedClasses from "../style.module.scss";

import ArrowDown from "assets/images/icons/JSX/arrow-down";
import SharedUpgrade from "assets/images/icons/Shared-model-blue.svg";

export const DropDown = ({
    className,
    options,
    selected = {},
    onChange,
    open,
    toggleOpen,
    search = false,
    searchPlaceholder,
    footer: Footer,
    dropDownWidth,
    emptyMessage = "No options meet the requirements.",
    isFlatDesign = false,
}) => {
    const listRef = useRef();

    const [filter, changeFilter] = useState("");
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    //comment
    const filteredOptions = options.filter(({ label = "" }) => {
        if (!search) {
            return true;
        }
        return label.toString().toLowerCase().includes(filter.toLowerCase());
    });

    // Collect tooltip IDs for disabled options with tooltips
    // Use a more stable ID based on the option value to avoid conflicts
    // Create a map of index to tooltip ID for easy lookup in Row component
    const tooltipIdMap = new Map();
    filteredOptions.forEach((option, index) => {
        if (option.disabled && option.disabledTooltip) {
            const stableId = option.value?.type || option.value?.id || option.label || `option-${index}`;
            const tooltipId = `disabled-tooltip-${stableId}-${index}`;
            tooltipIdMap.set(index, tooltipId);
        }
    });
    const tooltipIds = Array.from(tooltipIdMap.values());

    const dropdownClassNames = [classes.dropDown, open && classes.open, className && className].filter((clss) => clss);

    const getItemSize = (index) => {
        const { labelTag } = filteredOptions[index];

        if (screenWidth < 1480) {
            return labelTag ? 56 : 35;
        }

        return 36;
    };

    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [filteredOptions]);

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const Row = ({ index, style }) => {
        const {
            value,
            label,
            labelIcon: Icon = null,
            labelActionComponent: ActionComponent = null,
            labelTag = "",
            disabled = false,
            disabledTooltip = null,
        } = filteredOptions[index];

        const { isFromOrg = false } = value || {};
        // Get tooltip ID from the map to ensure consistency
        const tooltipId = disabled && disabledTooltip ? tooltipIdMap.get(index) : null;

        const rowClassNames = [
            classes.option,
            isEqual(selected, value) && classes.selected,
            disabled && classes.disabled,
            disabled && disabledTooltip && classes.disabledWithTooltip,
        ].filter((c) => c);

        return (
            <li
                key={index}
                className={rowClassNames.join(" ")}
                style={{ ...style, width: "97%", zIndex: 5 }}
                onClick={() => {
                    if (disabled) {
                        return;
                    }
                    onChange(value);
                    toggleOpen(false);
                }}
                data-for={tooltipId}
                data-tip={
                    disabledTooltip ? `<span style="display:block; max-width:16rem;">${disabledTooltip}</span>` : null
                }
                data-html={!!disabledTooltip}
                {...createKeyboardShortcut({
                    onKeyDown: () => {
                        if (!disabled) {
                            onChange(value);
                            toggleOpen(false);
                        }
                    },
                    dropDownNavigation: true,
                })}
            >
                {Icon && <Icon />}
                <div className={classes.label}>
                    {isFromOrg ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "15px" }} key={value.id}>
                            {label}{" "}
                            <img
                                style={{ margin: 0, height: "20px", width: "20px" }}
                                src={SharedUpgrade}
                                alt="Org Logo"
                            />
                        </div>
                    ) : (
                        label
                    )}
                    {labelTag && <span className={classes.labelTag}>{labelTag}</span>}
                </div>
                {ActionComponent && <ActionComponent />}
            </li>
        );
    };

    return (
        <div className={dropdownClassNames.join(" ")} style={{ width: dropDownWidth }}>
            {search && (
                <DropdownSearch
                    className={classes.selectFieldSearch}
                    changeFilter={changeFilter}
                    filter={filter}
                    placeholder={searchPlaceholder}
                />
            )}
            {filteredOptions.length > 0 ? (
                <ul>
                    <List
                        height={Math.min(182, filteredOptions.length * 35)}
                        itemCount={filteredOptions.length}
                        itemSize={getItemSize}
                        width={"unset"}
                        ref={listRef}
                        style={{ overflowX: "hidden", margin: "0.5rem" }}
                    >
                        {Row}
                    </List>
                </ul>
            ) : (
                <div className={classes.emptyFilter}>
                    <span>{emptyMessage}</span>
                </div>
            )}
            {Footer && <Footer />}
            {open && tooltipIds.map((id) => <Tooltip key={id} id={id} delay className={classes.dropdownTooltip} />)}
        </div>
    );
};

const DropDownWithDataPicker = ({ range, setRange, className, open, options, value, selectDateRange, toggleOpen }) => {
    const [dataRange, setDataRange] = useState({
        value: value || "allTime",
        rangeForPicker: {
            from: range.start ? new Date(range.start * 1000) : null,
            to: range.end ? new Date(range.end * 1000) : null,
        },
        ...range,
    });

    const dropdownClassNames = [classes.dropDown, open && classes.open, className && className].filter((clss) => clss);

    useEffect(() => {
        if (open) {
            setDataRange({
                value: value || "allTime",
                rangeForPicker: {
                    from: range.start ? new Date(range.start * 1000) : null,
                    to: range.end ? new Date(range.end * 1000) : null,
                },
                ...range,
            });
        }
    }, [value, open]);

    useEffect(() => {
        const indicator = document.getElementById("verticalSelectIndicator");
        const selectedOption = document.getElementById(`option-${dataRange.value}`);

        if (indicator && selectedOption) {
            indicator.style.top = `${selectedOption.offsetTop}px`;
        }
    }, [dataRange]);

    const handleDayClick = (day) => {
        const newRange = DateUtils.addDayToRange(day, dataRange.rangeForPicker);

        const from = newRange.from ? getTimeInSeconds(new Date(newRange.from.setHours(0, 0, 0, 0))) : null;
        const to = newRange.to ? getTimeInSeconds(new Date(newRange.to.setHours(23, 59, 59, 999))) : null;

        setDataRange((prev) => ({
            ...prev,
            value: "custom",
            rangeForPicker: newRange,
            start: from,
            // if the same day we set end to end of the day instead of start of the day like from
            end: to,
        }));
    };

    const handleApply = () => {
        selectDateRange(dataRange.value, dataRange);
        toggleOpen(false);
    };

    const handleClear = () => {
        selectDateRange("allTime");
    };

    const {
        rangeForPicker: { from, to },
    } = dataRange;

    const modifiers = {
        start: from,
        end: to,
        between:
            dataRange.rangeForPicker.from && dataRange.rangeForPicker.to
                ? { from: dataRange.rangeForPicker.from, to: dataRange.rangeForPicker.to }
                : null,
    };

    return (
        <div
            className={dropdownClassNames.join(" ")}
            style={{ width: "fit-content", padding: "24px 20px", display: "flex", minHeight: "402px" }}
        >
            <div className={classes.verticalOptionsContainer}>
                {/* OPTIONS */}
                {options.map((option) => (
                    <div
                        onClick={() => {
                            const newDataRange = getRangeForFilters(option.value, {
                                start: dataRange.start,
                                end: dataRange.end,
                            });

                            setDataRange(() => ({
                                ...newDataRange,
                                rangeForPicker: {
                                    from: newDataRange.start ? new Date(newDataRange.start * 1000) : null,
                                    to: newDataRange.end ? new Date(newDataRange.end * 1000) : null,
                                },
                                value: option.value,
                            }));
                        }}
                        id={`option-${option.value}`}
                        key={option.value}
                        className={classNames(classes.option, {
                            [classes.selectedOption]: dataRange.value === option.value,
                        })}
                    >
                        <span>{option.label}</span>
                    </div>
                ))}
                <div id="verticalSelectIndicator" className={classes.verticalSelectIndicator} />
            </div>
            <div className={classes.divider} />
            <div className={classes.datePickerContainer}>
                <div className={classes.dateRangeContainer}>
                    <span className={classes.dateRangeLabel}>Select range:</span>
                    <div className={classes.dateRangeValuesWrapper}>
                        <span>
                            {dataRange.start ? moment(dataRange.start * 1000).format("MMM D, YYYY") : "Start Date"}
                        </span>
                        <div className={classes.divider} />
                        <span>{dataRange.end ? moment(dataRange.end * 1000).format("MMM D, YYYY") : "End Date"}</span>
                    </div>
                </div>
                <div>
                    <DayPicker selectedDays={[from, { from, to }]} modifiers={modifiers} onDayClick={handleDayClick} />
                </div>
                <div className={classes.datePickerFooter}>
                    <Button small type="hollow" onClick={handleClear}>
                        Clear
                    </Button>
                    <Button small onClick={handleApply}>
                        Apply
                    </Button>
                </div>
            </div>
        </div>
    );
};

const Select = ({
    name,
    label,
    input,
    className,
    customLabelClass,
    large,
    compact = false,
    highlight = false,
    isStat = false,
    transparent = false,
    placeholder = "Select",
    hideLabel = false,
    options = [],
    disabled = false,
    updateSelected,
    info = "",
    meta = {},
    forcedLabel = "",
    parentError = false,
    search = false,
    searchPlaceholder = "",
    footer = null,
    dropDownWidth = "100%",
    isLoading = false,
    warning = "",
    nonH2kParam = "",
    emptyMessage,
    compassProps,
    forcedCustomLabel = false,
    customLabel = null,
    displaySelectedValue = false,
    customStyles = {},
    isDatePicker = false,
    range = { start: null, end: null },
    setRange = () => {},
    selectDateRange = () => {},
    inputClassName,
    inputStyles = {},
    labelStyle = {},
    embeddedInCarbonRow = false,
    isFlatDesign = false,
    borderBottom = false,
    customBorderRadius = {},
    withFullBorder = false,
    withRightBorder = false,
    tabIndex = 0,
    isNewDesign = false,
}) => {
    const { invalid = false, error = "", touched = false } = meta;
    const classNamesList = [
        classes.selectField,
        sharedClasses.inputField,
        isFlatDesign && sharedClasses.isFlatDesign,
        className && className,
        large && sharedClasses.large,
        compact && sharedClasses.compact,
        highlight && !disabled && sharedClasses.highlight,
        isStat && sharedClasses.smallStat,
        transparent && sharedClasses.transparent,
        disabled && classes.disabled,
        hideLabel && classes.labelHidden,
        (parentError || invalid) && sharedClasses.invalid,
        warning && classes.warning,
        embeddedInCarbonRow && classes.embeddedInCarbonRow,
        embeddedInCarbonRow && sharedClasses.embeddedInCarbonRow,
    ].filter((c) => c);

    const { value = "", onChange } = input;
    const [open, toggleOpen] = useState(false);
    const [selected, toggleSelected] = useState("");
    const ref = useOutsideClickHook(() => toggleOpen(false));
    const newDesignLabelFloating = open || !!selected;

    const handleChange = (newValue) => {
        if (updateSelected) {
            updateSelected(newValue);
        }
        onChange(newValue);
    };

    useEffect(() => {
        if (forcedLabel) {
            return toggleSelected(forcedLabel);
        }
        const { label: selectedLabel } = options.find(({ value: val }) => isEqual(val, value)) || {};
        toggleSelected(selectedLabel);
    }, [value, options]);

    return (
        <div
            ref={ref}
            className={classNamesList.join(" ") + " prevent-outline"}
            aria-label={embeddedInCarbonRow && label ? label : undefined}
            style={{
                ...(borderBottom ? { borderBottom: "1px solid #E0E0E0" } : {}),
                ...customBorderRadius,
                ...(withFullBorder ? { border: "1px solid #E0E0E0" } : {}),
                ...(withRightBorder ? { borderRight: "1px solid #E0E0E0" } : {}),
            }}
            tabIndex={disabled ? -1 : tabIndex}
            onKeyDown={(e) => !disabled && onKSelect(e, open, toggleOpen, search)}
            // style={customStyles}
        >
            <div className={classNames(sharedClasses.fieldHeader)}>
                {label && (
                    <label className={hideLabel ? "invisible" : ""} htmlFor={name} style={labelStyle}>
                        {label}
                    </label>
                )}
                {info && !isStat && (
                    <InfoTooltip
                        className={sharedClasses.infoTooltip}
                        info={info}
                        width={isFlatDesign ? "12px" : "16px"}
                        height={isFlatDesign ? "12px" : "16px"}
                    />
                )}
                {warning && !isStat && (
                    <WarningTooltip
                        className={classNames(sharedClasses.warningTooltip, { [sharedClasses.pushLeft]: info })}
                        warning={warning}
                    />
                )}
                {nonH2kParam && !isStat && (
                    <NonH2kParamTooltip
                        className={classNames(sharedClasses.nonH2kParamTooltip, { [sharedClasses.pushLeft]: warning })}
                        nonH2kParam={nonH2kParam}
                    />
                )}
            </div>
            {isLoading && (
                <div className={sharedClasses.loading}>
                    <LoadingDots />
                </div>
            )}
            {!isLoading &&
                (isNewDesign ? (
                    <div className={sharedClasses.newDesignInputWrap}>
                        <span
                            className={classNames(sharedClasses.newDesignLabel, {
                                [sharedClasses.activeNewDesignLabel]: newDesignLabelFloating,
                            })}
                        >
                            {label}
                        </span>
                        <div
                            className={classNames(sharedClasses.selectInput, classes.value, inputClassName, {
                                [classes.open]: open,
                                [classes.disabled]: disabled,
                                [sharedClasses.disabled]: disabled,
                                [classes.hasCompass]: compassProps,
                            })}
                            style={{
                                ...(isFlatDesign ? { border: "none", borderRadius: 0 } : {}),
                                ...inputStyles,
                            }}
                            onClick={() => !disabled && toggleOpen(!open)}
                        >
                            {selected ? (
                                <span className={`${classes.selected} ${customLabelClass ? customLabelClass : ""}`}>
                                    {selected}
                                </span>
                            ) : (
                                <span className={classes.placeholder}>&nbsp;</span>
                            )}
                            <ArrowDown />
                        </div>
                    </div>
                ) : (
                    <div
                        className={classNames(sharedClasses.selectInput, classes.value, inputClassName, {
                            [classes.open]: open,
                            [classes.disabled]: disabled,
                            [sharedClasses.disabled]: disabled,
                            [classes.hasCompass]: compassProps,
                        })}
                        style={{
                            ...(isFlatDesign ? { border: "none", borderRadius: 0 } : {}),
                            ...inputStyles,
                        }}
                        onClick={() => !disabled && toggleOpen(!open)}
                    >
                        {compassProps && (
                            <div className={classes.orientation}>
                                <Compass {...compassProps} />
                            </div>
                        )}
                        {selected ? (
                            <span className={`${classes.selected} ${customLabelClass ? customLabelClass : ""}`}>
                                {selected}
                            </span>
                        ) : (
                            <span className={classes.placeholder}>{placeholder}</span>
                        )}{" "}
                        {displaySelectedValue && selected && (
                            <span className={classes.displaySelected}>
                                {options.find((opt) => opt.value === value)?.label}
                            </span>
                        )}
                        <ArrowDown />
                    </div>
                ))}
            {!isLoading && !isDatePicker && (
                <DropDown
                    className={sharedClasses.dropDown}
                    options={options}
                    selected={value}
                    onChange={handleChange}
                    open={open}
                    toggleOpen={toggleOpen}
                    search={search}
                    searchPlaceholder={searchPlaceholder}
                    footer={footer}
                    dropDownWidth={dropDownWidth}
                    emptyMessage={emptyMessage}
                    isFlatDesign={isFlatDesign}
                />
            )}

            {!isLoading && isDatePicker && open && (
                <DropDownWithDataPicker
                    range={range}
                    setRange={setRange}
                    className={sharedClasses.dropDown}
                    options={options}
                    open={open}
                    toggleOpen={toggleOpen}
                    value={value}
                    selected={selected}
                    selectDateRange={selectDateRange}
                />
            )}
            {!transparent && !isStat && invalid && error && typeof error === "string" && (
                <span className={sharedClasses.errorMessage}>{error}</span>
            )}

            {/* Due to the way the manual-scroll is handled, sass won't work so it needs an in-comp style */}
            <style>
                {`
                .prevent-outline:focus { outline: none !important; }
                .manual-focus {border: 1px solid #62bcf8 !important;}
                `}
            </style>
        </div>
    );
};

export default Select;
